import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { ThemeProvider } from './components/ThemeContext';
import { I18nProvider } from './components/I18nContext';
import { AuthProvider } from './components/AuthContext';
import { RequireAuth } from './components/RequireAuth';
import { RequireAdmin } from './components/RequireAdmin';

// Eagerly loaded — above the fold / critical path
import { Home } from './pages/Home';

// Lazy-loaded — only fetched when the user navigates to these routes
const About         = lazy(() => import('./pages/About').then(m => ({ default: m.About })));
const ServiceDetail = lazy(() => import('./pages/ServiceDetail').then(m => ({ default: m.ServiceDetail })));
const Contact       = lazy(() => import('./pages/Contact').then(m => ({ default: m.Contact })));
const Blog          = lazy(() => import('./pages/Blog').then(m => ({ default: m.Blog })));
const BlogPost      = lazy(() => import('./pages/BlogPost').then(m => ({ default: m.BlogPost })));
const Referenzen    = lazy(() => import('./pages/Referenzen').then(m => ({ default: m.Referenzen })));
const Impressum     = lazy(() => import('./pages/Impressum').then(m => ({ default: m.Impressum })));
const Datenschutz   = lazy(() => import('./pages/Datenschutz').then(m => ({ default: m.Datenschutz })));
const Login         = lazy(() => import('./pages/Login').then(m => ({ default: m.Login })));
const Dashboard     = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard').then(m => ({ default: m.AdminDashboard })));

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <ThemeProvider>
        <I18nProvider>
          <Router>
            <ScrollToTop />
            <div className="flex flex-col min-h-screen bg-slate-50 dark:bg-[#020617] text-slate-900 dark:text-slate-100 font-sans transition-colors duration-300 selection:bg-[#0ea5e9] selection:text-white">
              <Navbar />
              <main className="flex-grow">
                <Suspense fallback={
                  <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-[#020617]">
                    <div className="w-6 h-6 rounded-full border-2 border-[#0ea5e9] border-t-transparent animate-spin" />
                  </div>
                }>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/services/:type" element={<ServiceDetail />} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/:id" element={<BlogPost />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/referenzen" element={<Referenzen />} />
                  <Route path="/impressum" element={<Impressum />} />
                  <Route path="/datenschutz" element={<Datenschutz />} />
                  <Route path="/privacy" element={<Datenschutz />} />
                  <Route path="/login" element={<Login />} />

                  {/* Protected User Dashboard */}
                  <Route
                    path="/dashboard"
                    element={
                      <RequireAuth>
                        <Dashboard />
                      </RequireAuth>
                    }
                  />

                  {/* Protected Admin Dashboard */}
                  <Route
                    path="/admin"
                    element={
                      <RequireAdmin>
                        <AdminDashboard />
                      </RequireAdmin>
                    }
                  />

                  {/* Fallback routes */}
                  <Route path="*" element={<Home />} />
                </Routes>
                </Suspense>
              </main>
              <Footer />
            </div>
          </Router>
        </I18nProvider>
      </ThemeProvider>
    </AuthProvider>
  );
};

export default App;